import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import type { AuthAudience } from '@repo/common';
import type { Role } from '@repo/frontend-shared';
import { buildLoginRedirectHref } from '@repo/frontend-shared/utils';
import { Loader } from '@repo/ui/components/elements/Loader/Loader';
import { useCurrentUser } from '@repo/ui/contexts/current-user-context';

interface RequireRoleProps {
  loginRoute: string;
  roles: readonly Role[];
  audience?: AuthAudience;
  preserveRedirect?: boolean;
  children?: ReactNode;
}

export function RequireRole({ loginRoute, roles, preserveRedirect = false, children }: RequireRoleProps) {
  const location = useLocation();
  const { currentUser, loading } = useCurrentUser();
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) {
        setLoadingTimeout(true);
      }
    }, 15000);

    return () => clearTimeout(timer);
  }, [loading]);

  if (loading && !loadingTimeout) {
    return <Loader />;
  }

  if (!currentUser) {
    const redirectRoute = preserveRedirect ? buildLoginRedirectHref(loginRoute, location) : loginRoute;
    return <Navigate to={redirectRoute} replace />;
  }

  if (!roles.includes(currentUser.role)) {
    return <Navigate to={loginRoute} replace />;
  }

  if (children === undefined) {
    return <Outlet />;
  }

  return <>{children}</>;
}
